import { FC } from 'react';
import * as React from 'react';
import { graphql, createRefetchContainer, RelayRefetchProp } from 'react-relay/legacy';

import { PdpSharedShippingInfo } from '../PdpSharedShippingInfo/PdpSharedShippingInfo';
import { PdpSharedShippingInfoLink } from '../PdpSharedShippingInfo/PdpSharedShippingInfoLink';
import { PdpSharedDetailShipsFrom } from './PdpSharedDetailShipsFrom';

import styles from './PdpSharedDetail.scss';

import { PdpSharedDetailShipping_item as ItemType } from './__generated__/PdpSharedDetailShipping_item.graphql';
import { PdpSharedDetailShipping_viewer as ViewerType } from './__generated__/PdpSharedDetailShipping_viewer.graphql';
import { PdpSharedDetailShipping_user as UserType } from './__generated__/PdpSharedDetailShipping_user.graphql';
import { PdpSharedDetailShipping_shipmentQuote as ShipmentQuoteType } from './__generated__/PdpSharedDetailShipping_shipmentQuote.graphql';
import { PdpSharedDetailShipping_shipment as ShipmentType } from './__generated__/PdpSharedDetailShipping_shipment.graphql';

type Props = {
    item: ItemType;
    viewer: ViewerType;
    user: UserType;
    itemId?: string;
    userId?: string;
    currency: string;
    shipmentQuote: ShipmentQuoteType;
    shipment: ShipmentType;
    relay: RelayRefetchProp;
    displayInline: boolean;
    preLinkQuoteDetails: React.ReactNode;
    postLinkQuoteDetails: React.ReactNode;
    isClient: boolean;
};

export const PdpSharedDetailsShippingComponent: FC<Props> = ({
    item,
    itemId,
    viewer,
    currency,
    user,
    userId,
    shipmentQuote,
    shipment,
    relay,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    isClient,
}) => {
    const { regionalInfo, relevantShipping } = viewer || {};
    const quoteRequestStatus = relevantShipping?.quoteRequestStatus;
    const showQuoteDetails = quoteRequestStatus !== 'requestQuote';
    const leadTime = item?.attributes?.leadTime?.[0]?.option?.urlLabel;

    return (
        <span className={styles.shippingInfoContainer} data-tn="pdp-spec-shipping">
            <PdpSharedShippingInfo
                user={user}
                item={item}
                regionalInfo={regionalInfo || null}
                currency={currency}
                shipmentQuote={shipmentQuote}
                shipment={shipment}
                leadTime={leadTime}
                quoteRequestStatus={quoteRequestStatus}
                renderTooltip
                noLineBreaks
                showCustomsInfo
                isClient={isClient}
            >
                {preLinkQuoteDetails && preLinkQuoteDetails}
                <PdpSharedShippingInfoLink
                    currency={currency}
                    shipmentQuote={shipmentQuote}
                    userId={userId}
                    item={item}
                    itemId={itemId}
                    user={user}
                    viewer={viewer}
                    showQuoteDetails={showQuoteDetails}
                    quoteRequestStatus={quoteRequestStatus}
                    onSuccessfulQuoteRequest={relay.refetch}
                    displayInline={displayInline && showQuoteDetails}
                />
                {postLinkQuoteDetails && postLinkQuoteDetails}
            </PdpSharedShippingInfo>
            <PdpSharedDetailShipsFrom item={item} />
        </span>
    );
};

export const PdpSharedDetailShipping = createRefetchContainer(
    PdpSharedDetailsShippingComponent,
    {
        viewer: graphql`
            fragment PdpSharedDetailShipping_viewer on Viewer {
                ...PdpSharedShippingInfoLink_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    quoteRequestStatus
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...PdpSharedShippingInfo_regionalInfo
                }
            }
        `,
        user: graphql`
            fragment PdpSharedDetailShipping_user on User {
                ...PdpSharedShippingInfoLink_user
                ...PdpSharedShippingInfo_user
            }
        `,
        item: graphql`
            fragment PdpSharedDetailShipping_item on Item {
                ...PdpSharedShippingInfo_item
                ...PdpSharedShippingInfoLink_item
                ...PdpSharedDetailShipsFrom_item
                hasShipmentQuotes
                hasPreQuotedShipping
                itemIsOnHold: isOnHold(buyerId: $userId)
                attributes {
                    leadTime {
                        option {
                            urlLabel
                        }
                    }
                }
                ...freeShippingHelpers_item
            }
        `,
        shipmentQuote: graphql`
            fragment PdpSharedDetailShipping_shipmentQuote on ShipmentQuote {
                isCustom
                isNew
                status
                ...PdpSharedShippingInfoLink_shipmentQuote
                ...PdpSharedShippingInfo_shipmentQuote
            }
        `,
        shipment: graphql`
            fragment PdpSharedDetailShipping_shipment on Shipment {
                ...PdpSharedShippingInfo_shipment
            }
        `,
    },
    graphql`
        query PdpSharedDetailShippingRefetchQuery(
            $userId: String!
            $fetchUser: Boolean!
            $itemId: String!
            $userZipCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $userCountryCode: String = ""
        ) {
            viewer {
                ...PdpSharedDetailShipping_viewer
                relevantShipping(itemId: $itemId, buyerId: $userId) {
                    shipmentQuote {
                        ...PdpSharedDetailShipping_shipmentQuote
                    }
                    shipment {
                        ...PdpSharedDetailShipping_shipment
                    }
                }
            }
        }
    `
);
